import { useState, useEffect, useCallback, useRef } from "react"
import { useForm, FormProvider } from "react-hook-form"
import { Box } from "@mui/material"
import { useDispatch, addNode, removeNode, useSelector } from "@ggs/store"

import type { FC } from ".."
import { BlogArticleCollection, BlogSearch, Container, Pagination, SearchFacets } from ".."
import type { BlogArticleTeaserProps } from "../BlogArticleTeaser/BlogArticleTeaser"
import { useArticleSearchData } from "../../local/hooks"
import {
  getLabelFromString,
  getIdFromString,
  formatSearchData,
  setFormFields,
} from "../TripGeneralSearch/helpers"
import { mapWishlistData, mapArticleTeaser } from "../../helpers/data-mappers"
import { useUrlParams } from "@ggs/hooks"

type BlogArticleSearchProps = {
  count?: number
  items: BlogArticleTeaserProps[]
  categoryId: string
  path: string
}

const BlogArticleSearch: FC<BlogArticleSearchProps> = ({ categoryId, path }) => {
  const dispatch = useDispatch()
  const wishlist = useSelector((state) => state.views.wishlist)
  const likedArticles = wishlist?.article && wishlist?.article.map((article: any) => article.nid)
  const { getArticleSolrData } = useArticleSearchData()
  const listRef = useRef<HTMLDivElement>(null)
  type SearchParams = NonNullable<Parameters<typeof getArticleSolrData>[number]>
  type SearchResults = Awaited<ReturnType<typeof getArticleSolrData>>

  const methods = useForm({
    defaultValues: {
      destination: null,
      country: null,
      experiences: [],
    },
  })

  const { reset, setValue, getValues } = methods

  const { params, setParam, resetParams } = useUrlParams(
    {
      destination: null,
      country: null,
      tripStyles: null,
      tripTypes: null,
      page: 1,
    },
    path
  )

  const PAGE_SIZE = 9
  const [articleSearchResults, setArticleSearchResults] = useState<SearchResults>({
    count: 0,
    totalPages: 0,
    results: [],
    destinations: [],
    countries: [],
    experiences: [],
  })

  const [searchParams, setSearchParams] = useState<any>({
    ...(params.destination && { destination: getIdFromString(params.destination as string) }),
    ...(params.country && { country: getIdFromString(params.country as string) }),
    ...(params.tripStyles && { tripStyles: getIdFromString(params.tripStyles as string) }),
    ...(params.tripTypes && { tripTypes: getIdFromString(params.tripTypes as string) }),
    ...(params.page && {
      pageIndex: Math.max((parseInt(params.page as string) - 1) * PAGE_SIZE, 0),
    }),
    pageSize: PAGE_SIZE,
  })
  const winObject = typeof window !== "undefined" ? window : null

  const setUrlParam = (value: any, type: string) => {
    if (value) {
      if (type === "experiences") {
        const tripTypes: string[] = []
        const tripStyles: string[] = []
        value.forEach((filter: any) => {
          const labelId = [filter.label, filter.value].join(":")
          filter?.experience === "Trip Types" ? tripTypes.push(labelId) : tripStyles.push(labelId)
        })
        if (tripTypes.length > 0) setParam("tripTypes", tripTypes.join(","))
        else setParam("tripTypes", null)
        if (tripStyles.length > 0) setParam("tripStyles", tripStyles.join(","))
        else setParam("tripStyles", null)
      } else {
        const labelId = [value.label, value.value].join(":")
        setParam(type, labelId)
      }
    } else {
      setParam(type, null)
    }
    setParam("page", 1)
  }

  const onApplyFilter = useCallback((filter: any, type: string) => {
    // set new url params
    setUrlParam(filter, type)
    // apply new filtering
    if (type === "experiences") {
      const tripStyles: string[] = []
      const tripTypes: string[] = []
      filter.forEach((item: any) => {
        if (item?.experience === "Trip Styles") tripStyles.push(item?.value)
        else tripTypes.push(item?.value)
      })
      setSearchParams((params: any) => ({
        ...params,
        tripStyles: tripStyles.join(","),
        tripTypes: tripTypes.join(","),
      }))
    } else {
      setSearchParams((params: any) => ({ ...params, [type]: filter?.value || null }))
    }
  }, [])

  const onPageChange = useCallback((page: number) => {
    const pageIndex = Math.max((page - 1) * PAGE_SIZE, 0)
    setParam("page", `${page}`)
    setSearchParams((params: any) => ({
      ...params,
      pageIndex,
    }))
    // resolve scroll top when page changes
    if (listRef.current && winObject) {
      const listItemRect = listRef.current.getBoundingClientRect()
      const topOffset = winObject.scrollY + listItemRect.top - 150
      winObject.scrollTo({ top: topOffset, behavior: "smooth" })
    }
  }, [])

  const removeAllFilters = useCallback(() => {
    const defaultValues = {
      destination: null,
      country: null,
      tripStyles: null,
      tripTypes: null,
      pageIndex: 0,
    }
    setSearchParams((params: any) => ({ ...params, ...defaultValues }))
    // reset URL query params
    resetParams({ page: params.page })
    // reset form fields
    reset()
    // change to first page
    setParam("page", 1)
  }, [])

  const getUsedFilters = () => {
    const facets = []
    if (params?.destination)
      facets.push({ label: getLabelFromString(params?.destination as string), type: "destination" })
    if (params?.country)
      facets.push({ label: getLabelFromString(params?.country as string), type: "country" })
    if (params?.tripStyles) {
      const styles = getLabelFromString(params?.tripStyles as string).split(",")
      styles?.forEach((style: string) =>
        facets.push({ label: style, type: "experience", experienceType: "tripStyles" })
      )
    }
    if (params?.tripTypes) {
      const types = getLabelFromString(params?.tripTypes as string).split(",")
      types?.forEach((type: string) =>
        facets.push({ label: type, type: "experience", experienceType: "tripTypes" })
      )
    }
    return facets
  }

  const removeIndividualFilter = (facet: any) => {
    if (facet.type === "experience") {
      const { experiences } = getValues()
      setValue(
        "experiences",
        experiences.filter((item: any) => item.label !== facet.label)
      )
      if (facet?.experienceType === "tripStyles") {
        const newParam = params?.tripStyles
          //@ts-ignore
          ?.split(",")
          ?.filter((value: string) => facet.label !== value.split(":")[0])

        setParam("tripStyles", newParam.join(","))
      }
      if (facet?.experienceType === "tripTypes") {
        const newParam = params?.tripTypes
          //@ts-ignore
          ?.split(",")
          ?.filter((value: string) => facet.label !== value.split(":")[0])

        setParam("tripTypes", newParam.join(","))
      }
    } else {
      setValue(facet?.type, null)
      setParam(facet?.type, null)
    }

    const newSearchParams = formatSearchData(getValues() as any)
    setSearchParams((params: any) => ({ ...params, ...newSearchParams, pageIndex: 0 }))
  }

  const fetchSolrArticleData = async (params: SearchParams) => {
    const data = await getArticleSolrData(params)
    const filteredData = mapWishlistData(likedArticles, data.results)
    setArticleSearchResults({ ...data, results: filteredData })
  }

  const addToWishList = (selectedEntity: any) => {
    const filteredState = mapArticleTeaser(articleSearchResults?.results, selectedEntity, true)
    setArticleSearchResults({ ...articleSearchResults, results: filteredState })
    dispatch(
      addNode({ type: selectedEntity.contentType, entity: selectedEntity, idPropertyKey: "nid" })
    )
  }

  const removeFromWishList = (selectedEntity: any) => {
    const filteredState = mapArticleTeaser(articleSearchResults?.results, selectedEntity, false)
    setArticleSearchResults({ ...articleSearchResults, results: filteredState })
    dispatch(removeNode({ type: selectedEntity.contentType, findPredicate: selectedEntity }))
  }

  useEffect(() => {
    fetchSolrArticleData({ ...searchParams, category: categoryId })
  }, [searchParams])

  useEffect(() => {
    const fields: any = setFormFields(params)
    Object.keys(fields).forEach((field: any) => {
      setValue(field, fields[field])
    })
  }, [])

  return (
    <Container maxWidth={1480}>
      <FormProvider {...methods}>
        <BlogSearch
          destinations={articleSearchResults.destinations}
          countries={articleSearchResults.countries}
          experiences={articleSearchResults.experiences}
          onApplyFilter={onApplyFilter}
        />
        <Box pt={5} pb={15}>
          <SearchFacets
            facets={getUsedFilters()}
            count={articleSearchResults.count}
            removeAllFilters={removeAllFilters}
            removeIndividualFilter={removeIndividualFilter}
            showSpecialsToggle={false}
          />
        </Box>
        <Box ref={listRef}>
          <BlogArticleCollection
            items={articleSearchResults.results as any}
            onLike={addToWishList}
            onUnlike={removeFromWishList}
          />
        </Box>
        {articleSearchResults.totalPages > 1 && (
          <Box pt={15} pb={{ xs: 15, md: 45 }}>
            <Pagination
              page={parseInt(params.page as string)}
              count={articleSearchResults.totalPages}
              onPageChange={onPageChange}
            />
          </Box>
        )}
      </FormProvider>
    </Container>
  )
}

export default BlogArticleSearch
